/**
 * 紧凑筛选组件
 * 以弹窗方式展示完整筛选条件，节省屏幕空间
 */

import React, { useState, useMemo, useCallback, useRef } from "react";
import { Button, Badge, Drawer, Input, Divider } from "antd";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useMonitor } from "../../context/MonitorContext";
import { FLIGHT_STATUS, FLIGHT_STATUS_LABELS } from "../../constants";
import AircraftSelector from "../../../../../components/common/AircraftSelector";
import UnifiedFlightFilter from "../UnifiedFlightFilter";

/**
 * 紧凑筛选组件
 * 默认显示为一行简洁的筛选摘要，点击展开完整筛选表单（复用UnifiedFlightFilter）
 */
const CompactFilter = () => {
    const { filters, updateFilters } = useMonitor();
    const [drawerVisible, setDrawerVisible] = useState(false);

    // 搜索框防抖
    const [searchInputValue, setSearchInputValue] = useState(filters.searchText || "");
    const searchDebounceRef = useRef(null);

    // Effect: 同步筛选条件到本地搜索框
    React.useEffect(() => {
        setSearchInputValue(filters.searchText || "");
    }, [filters]);

    // 摘要栏搜索框防抖处理
    const handleSummarySearchChange = useCallback(
        (e) => {
            const value = e.target.value;
            setSearchInputValue(value);
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = setTimeout(() => {
                updateFilters({ ...filters, searchText: value });
            }, 500);
        },
        [filters, updateFilters],
    );

    // 打开筛选抽屉
    const handleOpenDrawer = () => {
        setDrawerVisible(true);
    };

    const handleCloseDrawer = () => {
        setDrawerVisible(false);
    };

    // 计算筛选条件摘要
    const filterSummary = useMemo(() => {
        const parts = [];
        if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
            const start = dayjs.utc(filters.dateRange[0]).format("MM-DD");
            const end = dayjs.utc(filters.dateRange[1]).format("MM-DD");
            parts.push(`${start} ~ ${end}`);
        }
        if (filters.status && filters.status.length > 0) {
            if (filters.status.length === Object.keys(FLIGHT_STATUS).length) {
                parts.push("全部状态");
            } else {
                const statusLabels = filters.status.map((s) => FLIGHT_STATUS_LABELS[s] || s);
                parts.push(statusLabels.join("/"));
            }
        }
        if (filters.registrations && filters.registrations.length > 0) {
            parts.push(filters.registrations.length <= 2 ? filters.registrations.join(",") : `${filters.registrations.slice(0, 2).join(",")}+`);
        }
        if (filters.departure_airport) parts.push(`DEP:${filters.departure_airport}`);
        if (filters.arrival_airport) parts.push(`ARR:${filters.arrival_airport}`);
        if (filters.searchText) parts.push(`"${filters.searchText.substring(0, 8)}${filters.searchText.length > 8 ? "..." : ""}"`);
        return parts.length > 0 ? parts.join(" | ") : "点击设置筛选条件";
    }, [filters]);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.dateRange && filters.dateRange[0]) count++;
        if (filters.status && filters.status.length > 0 && filters.status.length !== Object.keys(FLIGHT_STATUS).length) count++;
        if (filters.registrations && filters.registrations.length > 0) count++;
        if (filters.departure_airport) count++;
        if (filters.arrival_airport) count++;
        if (filters.searchText) count++;
        return count;
    }, [filters]);

    return (
        <>
            {/* 紧凑筛选条 */}
            <div className="compact-filter-bar">
                <Button type="text" icon={<FilterOutlined />} onClick={handleOpenDrawer} size="small">
                    <span style={{ marginLeft: 4 }}>{filterSummary}</span>
                    {activeFilterCount > 0 && <Badge count={activeFilterCount} size="small" style={{ marginLeft: 6, backgroundColor: "#1890ff" }} />}
                </Button>
                <Divider type="vertical" style={{ margin: "0 8px" }} />
                {/* 飞机注册号快捷选择 */}
                <AircraftSelector
                    mode="multiple"
                    placeholder="选择飞机"
                    value={filters.registrations || []}
                    onChange={(value) => {
                        updateFilters({ ...filters, registrations: value || [] });
                    }}
                    maxTagCount={2}
                    displayMode="simple"
                    size="small"
                    style={{ width: 260 }}
                    allowClear
                />
                <Divider type="vertical" style={{ margin: "0 8px" }} />
                {/* 搜索框 - 带防抖 */}
                <Input
                    placeholder="搜索航班/机场"
                    prefix={<SearchOutlined />}
                    size="small"
                    style={{ width: 260, height: 24 }}
                    value={searchInputValue}
                    onChange={handleSummarySearchChange}
                    allowClear
                    onClear={() => {
                        setSearchInputValue("");
                        updateFilters({ ...filters, searchText: "" });
                    }}
                />
            </div>

            {/* 筛选抽屉 */}
            <Drawer
                title="筛选条件"
                placement="top"
                height="auto"
                width="100%"
                onClose={handleCloseDrawer}
                open={drawerVisible}
                styles={{
                    body: { paddingBottom: 24 },
                }}
            >
                <UnifiedFlightFilter />
            </Drawer>
        </>
    );
};

export default CompactFilter;
